// desktop-navigation.js
class DesktopNavigation extends HTMLElement {
  constructor() {
    super();
    this.delegate = new DomDelegate(this);
    this.openingTimeout = null;
    this.currentMegaMenu = null;
    this.lastHoveredIndex = 0;
    this.activeMenu = null;
  }

  connectedCallback() {
    this.initializeEventListeners();
  }

  initializeEventListeners() {
    // Handle regular dropdown hover
    this.addEventListener('mouseenter', (event) => {
      const target = event.target.closest('.has-dropdown');
      if (target && event.relatedTarget !== null) {
        this.openDropdown(target);
      }
    }, true);

    // Handle 3-column mega menu navigation hover
    this.addEventListener('mouseover', (event) => {
      const navItem = event.target.closest('.mega-menu-three-column__nav-item');
      if (navItem) {
        this.handleThreeColumnHover(navItem);
      }
    });

    this.addEventListener('click', (event) => {
      const target = event.target.closest('.header__linklist-link[aria-expanded], .nav-dropdown__link[aria-expanded]');
      if (target) {
        if (window.matchMedia("(hover: hover)").matches || target.getAttribute("aria-expanded") === "true") {
          return;
        }
        event.preventDefault();
        this.openDropdown(target.parentElement);
      }
    });

    // Shopify block events
    document.addEventListener('shopify:block:select', (event) => {
      if (event.target.parentElement && event.target.parentElement.closest('desktop-navigation')) {
        this.openDropdown(event.target.parentElement);
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      if (event.target.parentElement && event.target.parentElement.closest('desktop-navigation')) {
        this.closeDropdown(event.target.parentElement);
      }
    });
  }

  // Handle 3-column mega menu hover
  handleThreeColumnHover(navItem) {
    const megaMenu = navItem.closest('.mega-menu-three-column');
    if (!megaMenu) return;
    
    const index = navItem.getAttribute('data-index');
    this.lastHoveredIndex = index;
    
    // Remove active class from all items
    megaMenu.querySelectorAll('.mega-menu-three-column__nav-item').forEach(item => {
      item.classList.remove('active');
    });
    
    // Hide all content panels
    megaMenu.querySelectorAll('.mega-menu-three-column__content').forEach(content => {
      content.classList.remove('active');
    });
    
    // Add active class to hovered item
    navItem.classList.add('active');
    
    // Show corresponding content panel
    const contentPanel = megaMenu.querySelector(`.mega-menu-three-column__content[data-index="${index}"]`);
    if (contentPanel) {
      contentPanel.classList.add('active');
    }
  }

  openDropdown(parentElement) {
    const menuItem = parentElement.querySelector("[aria-controls]");
    if (!menuItem) return;
    
    const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);
    if (!dropdown) return;
    
    this.currentMegaMenu = dropdown.classList.contains("mega-menu") ? dropdown : null;
    
    // Clear any existing timeout
    if (this.openingTimeout) {
      clearTimeout(this.openingTimeout);
    }
    
    this.openingTimeout = setTimeout(() => {
      if (menuItem.getAttribute("aria-expanded") === "true") {
        return;
      }
      
      // Close any open dropdown first
      if (this.activeMenu && this.activeMenu !== parentElement) {
        this.closeDropdown(this.activeMenu);
      }
      
      menuItem.setAttribute("aria-expanded", "true");
      dropdown.removeAttribute("hidden");
      this.activeMenu = parentElement;
      
      // For 3-column mega menu, set first item as active
      if (dropdown.classList.contains("mega-menu-three-column")) {
        const firstNavItem = dropdown.querySelector('.mega-menu-three-column__nav-item');
        if (firstNavItem && !firstNavItem.classList.contains('active')) {
          this.handleThreeColumnHover(firstNavItem);
        }
      }
      
      // Animation for regular mega menu
      if (dropdown.classList.contains("mega-menu") && !dropdown.classList.contains("mega-menu-three-column") && !this.prefersReducedMotion()) {
        const items = Array.from(dropdown.querySelectorAll(".mega-menu__column, .mega-menu__image-push"));
        items.forEach((item) => {
          item.style.opacity = 0;
        });
        
        items.forEach((item, index) => {
          setTimeout(() => {
            item.style.transition = 'opacity 250ms cubic-bezier(0.65, 0, 0.35, 1), transform 250ms cubic-bezier(0.65, 0, 0.35, 1)';
            item.style.opacity = 1;
            item.style.transform = 'translateY(0)';
          }, 100 + 60 * index);
        });
      }
      
      // Mouse leave listeners
      const leaveListener = (event) => {
        if (event.relatedTarget !== null && !dropdown.contains(event.relatedTarget)) {
          this.closeDropdown(parentElement);
          parentElement.removeEventListener("mouseleave", leaveListener);
        }
      };
      
      const leaveDocumentListener = () => {
        this.closeDropdown(parentElement);
        document.removeEventListener("mouseleave", leaveDocumentListener);
      };
      
      parentElement.addEventListener("mouseleave", leaveListener);
      document.addEventListener("mouseleave", leaveDocumentListener);
      
      this.openingTimeout = null;
      this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:open", { bubbles: true }));
    }, 100);
    
    parentElement.addEventListener("mouseleave", () => {
      if (this.openingTimeout) {
        clearTimeout(this.openingTimeout);
        this.openingTimeout = null;
      }
    }, { once: true });
  }

  closeDropdown(parentElement) {
    const menuItem = parentElement.querySelector("[aria-controls]");
    if (!menuItem) return;
    
    const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);
    if (!dropdown) return;
    
    requestAnimationFrame(() => {
      dropdown.classList.add("is-closing");
      menuItem.setAttribute("aria-expanded", "false");
      
      setTimeout(() => {
        dropdown.setAttribute("hidden", "");
        if (this.openingTimeout) {
          clearTimeout(this.openingTimeout);
          this.openingTimeout = null;
        }
        dropdown.classList.remove("is-closing");
        if (this.activeMenu === parentElement) {
          this.activeMenu = null;
        }
      }, dropdown.classList.contains("mega-menu") && this.currentMegaMenu !== dropdown ? 250 : 0);
      
      this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:close", { bubbles: true }));
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

// DomDelegate helper class
class DomDelegate {
  constructor(element) {
    this.element = element;
    this.events = new Map();
  }

  on(eventName, selector, handler, useCapture = false) {
    const eventHandler = (event) => {
      const target = event.target.closest(selector);
      if (target && this.element.contains(target)) {
        handler(event, target);
      }
    };
    
    this.element.addEventListener(eventName, eventHandler, useCapture);
    
    if (!this.events.has(eventName)) {
      this.events.set(eventName, []);
    }
    this.events.get(eventName).push({ selector, handler, eventHandler });
  }
}

// Register custom element
if (!customElements.get('desktop-navigation')) {
  customElements.define('desktop-navigation', DesktopNavigation);
}